import React, { useState, useEffect, useContext, useRef, useMemo} from "react";
import { Outlet, useLocation, useNavigate, NavLink } from "react-router-dom";
import { Col, Container, Row } from "react-bootstrap";
import { FaArrowLeft, FaArrowRight, FaTimes } from "react-icons/fa";
import Header from "../../components/header";
import Footer from "../../components/footer";
import '../../styles/utility.css';
import { LanguageContext } from "../../contexts/LanguageContext";
import "../../assets/css/Layout.css";
import Cookies from 'js-cookie';
import { useCaseInfo } from "../../contexts/CaseInfoContext";
import { getApiService } from "../../services/ApiService";

const Layout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { pageContent } = useContext(LanguageContext);
    const [genericContent, setGenericContent] = useState([]);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isLayoutSidebarOpen, setIsLayoutSidebarOpen] = useState(false);
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
    const expandedWidth = "230px";
    const collapsedWidth = "80px";
    const [isManagePoliciesOpen, setIsManagePoliciesOpen] = useState(false);
    const [logotitle, setLogoTitle] = useState(''); //st48236 - 508 compliance
    const mainContent = document.getElementById('main-content');
    // Case Info Context
    const { caseInfo } = useCaseInfo();


    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
            if (window.innerWidth >= 768) {
                setIsMobileSidebarOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (windowWidth < 768) {
            setIsSidebarOpen(false);
        } else if (windowWidth >= 768 && windowWidth < 992) {
            setIsSidebarOpen(false);
        } else {
            setIsSidebarOpen(true);
        }
    }, [windowWidth]);

    let owningCarrier = Cookies.get('owningCarrier') || 'CG'; // Default to CG if not set

    useEffect(() => {
        if (pageContent?.generic) {
            setGenericContent(pageContent.generic);
        }

        switchTheme();
    }, [pageContent]);

    const [logoSrc, setLogoSrc] = useState('');
    const [smallLogoSrc, setSmallLogoSrc] = useState('');

    const isConnectiCareCarrier = Cookies.get("isConnectiCareCarrier");
    const connectiCareCarrier = Cookies.get("connectiCareCarrier");

    const switchTheme = async () => {

        if(isConnectiCareCarrier === 'true' && connectiCareCarrier !== null && connectiCareCarrier !== undefined){
            owningCarrier = connectiCareCarrier;
        }
        
        import('../../assets/images/carriers-logo/' + owningCarrier + '-sml.svg').then((image) => {
            setLogoSrc(image.default);
        });

        import('../../assets/images/carriers-logo/' + owningCarrier + '-sml-logo.svg').then((image) => {
            setSmallLogoSrc(image.default);
        });
    };
    
    const [userInteracted, setUserInteracted] = useState(false);
    const userInteractedRef = useRef(false);
    useEffect(() => {
        const handleUserInteraction = () => {
            userInteractedRef.current = true;
            setUserInteracted(true);
        };
        document.addEventListener('click', handleUserInteraction);
        document.addEventListener('keydown', handleUserInteraction);
        return () => {
            document.removeEventListener('click', handleUserInteraction);
            document.removeEventListener('keydown', handleUserInteraction);
        };
    }, []);

    useEffect(() => {
    let timeoutId;

    const focusMainContent = () => {
        const mainContent = document.getElementById('main-content');
        const activeEl = document.activeElement;
        const isEditingField =
            activeEl &&
            (activeEl.tagName === 'INPUT' ||
             activeEl.tagName === 'TEXTAREA' ||
             activeEl.tagName === 'SELECT' ||
             activeEl.isContentEditable);

        if (mainContent && !userInteractedRef.current && !isEditingField) {
        timeoutId = setTimeout(() => {
            requestAnimationFrame(() => {
            mainContent.focus({ preventScroll: true });
            });
        });
        }
    };

  focusMainContent();

  const handleSidebarInteraction = () => {
    if (!userInteractedRef.current) {
      timeoutId = setTimeout(() => {
        focusMainContent();
      }, 100);
    }
  };

  window.addEventListener("resize", handleSidebarInteraction);

  return () => {
    window.removeEventListener("resize", handleSidebarInteraction);
    if (timeoutId) clearTimeout(timeoutId);
  };
}, [location.pathname, isSidebarOpen, isMobileSidebarOpen]);


    const navigationItems = [
        { id: 1, name: genericContent?.sidebar_menu_overview_text, path: "/overview", icon: "fe-grid-r icon" },
        { 
            id: 2, 
            name: genericContent?.sidebar_menu_billing_text, 
            path: "/billing", 
            icon: "fe-file-r icon",
            subRoutes: ['/billing-summary', '/billing-confirmation'] // To keep Billing highlighted on summary page
        },
        { id: 3, name: genericContent?.sidebar_menu_coverage_text, path: "/coverage", icon: "fe-coverage-r icon" },
        { id: 4, name: genericContent?.sidebar_menu_edelivery_text, path: "/edelivery", icon: "fe-edev-r icon" },
        { id: 5, name: genericContent?.sidebar_menu_settings_text, path: "/settings", icon: "fe-settings-r icon", isLastItem: true },
    ];

    useEffect(() => {
        const IsMultipleIlb = localStorage.getItem('multipleILB');
        const IsCheckedFlag = localStorage.getItem('IsChecked');

        if (IsMultipleIlb === 'true' && IsCheckedFlag === 'true') {
            setIsLayoutSidebarOpen(true); // Set true if member has multiple ilb and is checked any case
        } else if (IsMultipleIlb === null && IsCheckedFlag === null) {
            setIsLayoutSidebarOpen(true); // Set true if member has single ilb
        }

        if (IsMultipleIlb === 'true') {
            setIsManagePoliciesOpen(true);
        }

        //st48236 - 508 compliance
        const logo_title_txt = "logo_title_"+owningCarrier;
        if(genericContent?.[logo_title_txt] !== undefined) {
            setLogoTitle(genericContent?.[logo_title_txt]);
        } else {
            if(owningCarrier === 'CG'){
                setLogoTitle("Cigna Member Portal");
            }
            if(owningCarrier === 'M1'){
                setLogoTitle("MHC Member Portal");
            }
            if(owningCarrier === 'Q5'){
                setLogoTitle("BCBSAZ Member Portal");
            }
            if(owningCarrier === 'SY'){
                setLogoTitle("Sentara Member Portal");
            }
            if(owningCarrier === 'XC'){
                setLogoTitle("Christus Member Portal");
            }
            if(owningCarrier === 'C4'){
                setLogoTitle("Molina Member Portal");
            }
        }
        //st48236 - 508 compliance

    }, [location.pathname]);

    const managePoliciesItems = [
        { id: 1, name: genericContent?.sidebar_menu_manage_policy_text, path: "/landing", icon: "fe-policy-r icon" }
    ];

    const pageTitles = {
        "/landing": genericContent?.layout_manage_policy_page_title,
        "/overview": genericContent?.layout_overview_page_title,
        "/billing": genericContent?.layout_billing_page_title,
        "/coverage": genericContent?.layout_coverage_page_title,
        "/edelivery": genericContent?.layout_edelivery_page_title,
        "/settings": genericContent?.layout_account_settings_page_title,
        "/billing-summary": genericContent?.layout_billing_details_page_title,
        "/billing-confirmation": genericContent?.layout_billing_confirmation_page_title,
        "/limitedproficiency": genericContent?.layout_lang_access_page_title,
        "/nondiscrimination": genericContent?.layout_non_disc_page_title,
        "/contactus": genericContent?.layout_contact_us_page_title,
        "/glossary": genericContent?.layout_glossary_page_title,
        "/faq": genericContent?.layout_faq_page_title,
    };

    const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
    const openMobileSidebar = () => setIsMobileSidebarOpen(true);
    const closeMobileSidebar = () => setIsMobileSidebarOpen(false);

    const isMobile = windowWidth < 768;
    const isMedium = windowWidth >= 768 && windowWidth < 992;
    const isDesktop = windowWidth >= 992;

    // PMO00003083 - Adding Banner Message in Side Bar
    const [displayImportantNotice, setDisplayImportantNotice] = useState(false);
    const displayImportantNoticeBanner = () => {
        const headers = {
            "X-Hps-Client-Id": process.env.REACT_APP_API_CLIENT_ID,
            "X-Hps-Secret-Id": process.env.REACT_APP_API_SECRET_ID,
        };
            const baseUrl = process.env.REACT_APP_API_BASE_URL;
            const targetRuleValueUrl = process.env.REACT_APP_API_GET_PROGRAM_RULE_VALUE_ENDPOINT;
            getApiService(
                baseUrl,
                targetRuleValueUrl,
                { action: "ByRule", rulename: "DISPLAY_IMPORTANT_NOTICE_MESSAGE", carrierCode: owningCarrier },
                headers
            ).then((response) => {
                if(response?.data && response.data.status === 'SUCCESS'){
                    const result = response.data.data
                    if(result === 'true'){
                        setDisplayImportantNotice(true);
                    }  
                }
            }).catch((error) =>{
                console.log(error);
            });
    }
    
    useEffect(() => {
        displayImportantNoticeBanner(); // return true or false
    }, [owningCarrier]);


    return (
        <Container fluid className="p-0">
            {/* Mobile Sidebar (only for small screens) */}
            {isMobile && (
                <div className="d-block d-md-none">
                    {/* Show arrow only for non-Q5 carriers - Q5 uses hamburger icon in header */}
                    {!isMobileSidebarOpen && owningCarrier !== 'Q5' && (
                        <FaArrowRight
                            className="open-arrow"
                            onClick={openMobileSidebar}
                            style={{ position: "fixed", left: 0, top: "0", zIndex: 1030 }}
                        />
                    )}
                    {isMobileSidebarOpen && owningCarrier === 'Q5' && (
                        <div
                            className="q5-mobile-sidebar-overlay"
                            onClick={closeMobileSidebar}
                        />
                    )}
                    <div className={`mobile-sidebar-slide ${isMobileSidebarOpen ? "open" : ""}`}>
                        <div className="mobile-sidebar-header">
                            <img
                                src={logoSrc}
                                alt="Logo"
                                className="mobile-logo"
                                onClick={() => navigate('/overview')}
                                style={{ cursor: 'pointer', width: 'auto' }}
                            />
                            {owningCarrier === 'Q5' ? (
                                <FaTimes 
                                    className="close-arrow q5-close-icon" 
                                    onClick={closeMobileSidebar} 
                                    aria-label="Close sidebar"
                                />
                            ) : (
                                <FaArrowLeft className="close-arrow" onClick={closeMobileSidebar} />
                            )}
                        </div>
                        <div className="bar">
                            <div className="pagelayout">
                                <div className="button-container sidebar-list">
                                    {isManagePoliciesOpen &&
                                        managePoliciesItems.map(item => (
                                            <NavLink
                                                key={item.id}
                                                to={item.path}
                                                className={({ isActive }) => {
                                                const isInSubRoute = item.subRoutes?.some(subRoute => 
                                                    location.pathname.startsWith(subRoute)
                                                );
                                                return `item-name hoverbutton ${isActive || isInSubRoute ? "action-color active" : ""}`;
                                                }}
                                                style={{ whiteSpace: 'nowrap' }}
                                            >
                                                <i className={item.icon}></i>
                                                {(isSidebarOpen || isDesktop) && <span className="item-text-label">{item.name}</span>}
                                            </NavLink>
                                        ))}

                                    {isLayoutSidebarOpen && !location.pathname.includes('/landing') &&
                                        navigationItems
                                            .filter(item => !item.isLastItem)
                                            .map(item => (
                                                <NavLink
                                                    key={item.id}
                                                    to={item.path}
                                                    className={({ isActive }) => {
                                                    const isInSubRoute = item.subRoutes?.some(subRoute => 
                                                        location.pathname.startsWith(subRoute)
                                                    );
                                                    return `item-name hoverbutton ${isActive || isInSubRoute ? "action-color active" : ""}`;
                                                    }}
                                                    onClick={closeMobileSidebar}
                                                >
                                                    <i className={item.icon}></i>
                                                    <span className="item-text-label">{item.name}</span>
                                                </NavLink>
                                            ))}
                                </div>
                            </div>
                            {// PMO00003083 - Adding Banner Message in Side Bar
                            displayImportantNotice && caseInfo?.owningCarrierType === "on-exchg" && (
                            <div className="notice ms-2 text-white">
                                <span>
                                    {genericContent?.sidebar_important_notice_msg || ""}
                                </span>
                            </div>
                            )}
                            <div className="last-button">
                                {isLayoutSidebarOpen && !location.pathname.includes('/landing') &&
                                    navigationItems
                                        .filter(item => item.isLastItem)
                                        .map(item => (
                                            <NavLink
                                                key={item.id}
                                                to={item.path}
                                                className={({ isActive }) => {
                                                const isInSubRoute = item.subRoutes?.some(subRoute => 
                                                    location.pathname.startsWith(subRoute)
                                                );
                                                return `item-name hoverbutton ${isActive || isInSubRoute ? "action-color active" : ""}`;
                                                }}
                                                onClick={closeMobileSidebar}
                                            >
                                                <i className={item.icon}></i>
                                                <span className="item-text-label">{item.name}</span>
                                            </NavLink>
                                        ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Row className="m-0 bg-logo g-0">
                {/* Desktop/Medium Sidebar Column (hidden on mobile) */}
                {!isMobile && (
                    <Col
                        sm="auto"
                        md="auto"
                        lg="auto"
                        xl="auto"
                        xxl="auto"
                        className={`p-0 d-none d-md-block sidebar-col ${isSidebarOpen ? "" : "collapsed-sidebar"}`}
                        style={{
                            position: 'fixed',
                            height: '100vh',
                            width: isSidebarOpen ? expandedWidth : collapsedWidth,
                            minWidth: isSidebarOpen ? expandedWidth : collapsedWidth,
                            transition: "width 0.3s ease",
                            overflow: "hidden",
                            zIndex: 1030,
                            backgroundColor: "#ffffff",
                        }}
                    >
                        <div className="sidebar custom-sidebar sidebar-default sidebar-white sidebar-base navs-rounded-all h-100">
                            <div className="img-container h-100">
                                <div className="main-Logo my-3">
                                    {isSidebarOpen ? (
                                        <img
                                            tabindex="0" //st48236 - 508 compliance
                                            src={logoSrc}
                                            className="logo-expanded"
                                            alt={logotitle || "Member Portal"} //st48236 - 508 compliance
                                            onClick={() => navigate('/overview')}
                                        />
                                    ) : (
                                        <img
                                            tabindex="0" //st48236 - 508 compliance
                                            src={smallLogoSrc}
                                            className="logo-collapsed w-90"
                                            alt={logotitle || "Member Portal"}
                                            onClick={() => navigate('/overview')}
                                            style={{ cursor: 'pointer', width: 'auto', height: '45px' }}
                                        />
                                    )}
                                </div>

                                <div className="sidebar-body pt-0 data-scrollbar">
                                    <div className="bar">
                                        <div className="pagelayout">
                                            <div className="button-container sidebar-list">
                                                {isManagePoliciesOpen &&
                                                    managePoliciesItems.map(item => (
                                                        <NavLink
                                                            key={item.id}
                                                            to={item.path}
                                                            className={({ isActive }) => {
                                                            const isInSubRoute = item.subRoutes?.some(subRoute => 
                                                                location.pathname.startsWith(subRoute)
                                                            );
                                                            return `item-name hoverbutton ${isActive || isInSubRoute ? "action-color active" : ""}`;
                                                            }}
                                                        >
                                                            <i className={item.icon}></i>
                                                            {(isSidebarOpen || isDesktop) && <span className="item-text-label">{item.name}</span>}
                                                        </NavLink>
                                                    ))}

                                                {isLayoutSidebarOpen && !location.pathname.includes('/landing') &&
                                                    navigationItems
                                                        .filter(item => !item.isLastItem)
                                                        .map(item => (
                                                            <NavLink
                                                                key={item.id}
                                                                to={item.path}
                                                                className={({ isActive }) => {
                                                                const isInSubRoute = item.subRoutes?.some(subRoute => 
                                                                    location.pathname.startsWith(subRoute)
                                                                );
                                                                return `item-name hoverbutton ${isActive || isInSubRoute ? "action-color active" : ""}`;
                                                                }}
                                                            >
                                                                <i className={item.icon}></i>
                                                                {(isSidebarOpen || isDesktop) && <span className="item-text-label">{item.name}</span>}
                                                            </NavLink>
                                                        ))}
                                            </div>
                                        </div>
                                        
                                        { //PMO00003083 - Adding Banner Message in Side Bar
                                        displayImportantNotice && caseInfo?.owningCarrierType === "on-exchg" && (
                                        <div className="notice ms-2 text-white">
                                            <span>
                                                {genericContent?.sidebar_important_notice_msg || ""}
                                            </span>
                                        </div>
                                        )}

                                        <div className="last-button">
                                            {isLayoutSidebarOpen && !location.pathname.includes('/landing') && 
                                                navigationItems
                                                    .filter(item => item.isLastItem)
                                                    .map(item => (
                                                        <NavLink
                                                            key={item.id}
                                                            to={item.path}
                                                            className={({ isActive }) => {
                                                            const isInSubRoute = item.subRoutes?.some(subRoute => 
                                                                location.pathname.startsWith(subRoute)
                                                            );
                                                            return `item-name hoverbutton ${isActive || isInSubRoute ? "action-color active" : ""}`;
                                                            }}
                                                        >
                                                            <i className={item.icon}></i>
                                                            {(isSidebarOpen || isDesktop) && <span className="item-text-label">{item.name}</span>}
                                                        </NavLink>
                                                    ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {isMedium && (
                            <div
                                onClick={toggleSidebar}
                                className="sidebar-toggle-arrow"
                                style={{
                                    position: "fixed",
                                    left: isSidebarOpen ? expandedWidth : collapsedWidth,
                                    top: "2%",
                                    transform: "translateX(-50%)",
                                    borderRadius: "50%",
                                    boxShadow: "0 0 8px rgba(0,0,0,0.2)",
                                    cursor: "pointer",
                                    zIndex: 1030,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    transition: "left 0.3s ease",
                                }}
                            >
                                {isSidebarOpen ? <FaArrowLeft size={12} /> : <FaArrowRight size={12} />}
                            </div>
                        )}
                    </Col>
                )}

                {/* Main Content Column */}
                <Col
                    md
                    sm
                    lg
                    xl
                    xxl
                    className="p-0 d-flex flex-column min-vh-100 maincontent-logo main-content-column"
                    style={{
                        marginLeft: isMobile ? 0 : (isSidebarOpen ? expandedWidth : collapsedWidth),
                        width: isMobile ? '100%' : (isSidebarOpen ? `calc(100% - ${expandedWidth})` : `calc(100% - ${collapsedWidth})`),
                        transition: "margin-left 0.3s ease, width 0.3s ease",
                    }}
                >
                    <Header
                        headerName={pageTitles[location.pathname]}
                        isSidebarOpen={isSidebarOpen}
                        onMenuClick={isMedium ? toggleSidebar : null}
                        onMobileSidebarToggle={openMobileSidebar}
                        isMobileSidebarOpen={isMobileSidebarOpen}
                        caseInfo={caseInfo}
                    />
                    
                    <main 
                        id="main-content"
                        tabIndex="-1"
                        className="flex-grow-1"
                        style={{ outline: 'none' }}
                        aria-label={pageTitles[location.pathname] || "Main Content"}
                    >
                    <Outlet />
                    </main>
                    <Footer caseInfo={caseInfo} />
                </Col>
            </Row>
        </Container>
    );
};

export default Layout;